import { Button, Grid } from '@mui/material';
import Table from 'components/shared/Table';
import TableCountLabel from 'components/shared/TableCountLabel';
import React, { Reducer, useCallback, useContext, useEffect, useMemo, useReducer, useState } from 'react';
import routes from 'store/configs/Routes';
import ApplicationStatus from 'store/enums/ApplicationStatus';
import Application from 'store/types/Application';
import { FilterConfig, SortConfig, TableColumn } from 'store/types/Table';
import { getStringValue, NUMBER_DATE_FORMAT } from 'util/Format';
import { defaultGridContainerProps, defaultGridItemProps, defaultSnackbarErrorProps } from 'util/Layout';
import { getHashRouteUrl } from 'util/Route';
import ApplicationsTableActionButton from 'components/applications/ApplicationsTableActionButton';
import reducer, {
  ApplicationsTableAction,
  ApplicationsTableActionType,
  ApplicationsTableState,
  initialState,
} from './UserApplicationsTableReducer';
import ApplicationsService from 'services/api/ApplicationsService';
import { useSnackbar } from 'notistack';
import DeleteOutlined from '@mui/icons-material/DeleteOutlined';
import ApplicationDecisionModal from 'components/applications/ApplicationDecisionModal';
import Modal from 'components/shared/Modal';
import { getApplicationStatusLabel } from 'util/Application';
import { ConfigContext } from 'components/ConfigGuard';
import { SiteConfigView } from 'store/types/SiteConfig';

import commonStyles from 'styles/common.module.scss';
import styles from './UserApplicationsTable.module.scss';

interface UserApplicationsTableProps {
  data?: Application[];
  loading?: boolean;
  filter: FilterConfig<Application>;
  showApplicant?: boolean;
  refetch: () => void;
}

const getFormattedList = (initialList: Application[]): Application[] => {
  const parents: Application[] = [];
  const childrenMap = new Map<string, Application[]>();

  for (const app of initialList) {
    if (app.parentId) {
      if (!childrenMap.has(app.parentId)) childrenMap.set(app.parentId, []);
      childrenMap.get(app.parentId)?.push(app);
    } else {
      parents.push(app);
    }
  }

  return parents.map((p) => ({ ...p, children: childrenMap.get(p.id) ?? [] }));
};

const UserApplicationsTable: React.FunctionComponent<UserApplicationsTableProps> = ({
  data = [],
  loading = false,
  filter,
  showApplicant = false,
  refetch,
}) => {
  const [deleteLoading, setDeleteLoading] = useState<boolean>();
  const { enqueueSnackbar } = useSnackbar();
  const [
    { sort, list = [], initialList = [], deleteModalOpen = false, decisionModalOpen = false, clickedItem },
    dispatch,
  ] = useReducer<Reducer<ApplicationsTableState, ApplicationsTableAction>>(reducer, initialState);

  const {
    modulesSettings: {
      applications: { applicationProgressCalculations },
    },
  }: SiteConfigView = useContext(ConfigContext);

  useEffect(() => {
    if (data) {
      dispatch({
        type: ApplicationsTableActionType.SetInitialList,
        payload: { initialList: getFormattedList(data) },
      });
    }
  }, [data]);

  useEffect(() => {
    dispatch({
      type: ApplicationsTableActionType.UpdateFilter,
      payload: { filter },
    });
  }, [filter]);

  const handleDeleteModalOpen = useCallback(
    (record: Application) => () => {
      dispatch({
        type: ApplicationsTableActionType.OpenDeleteModal,
        payload: { clickedItem: record },
      });
    },
    []
  );

  const handleDeleteModalClose = useCallback(() => {
    dispatch({
      type: ApplicationsTableActionType.CloseDeleteModal,
      payload: {},
    });
  }, []);

  const handleDecisionModalOpen = useCallback(
    (record: Application) => () => {
      dispatch({
        type: ApplicationsTableActionType.OpenDecisionModal,
        payload: { clickedItem: record },
      });
    },
    []
  );

  const handleDecisionModalClose = useCallback(() => {
    dispatch({
      type: ApplicationsTableActionType.CloseDecisionModal,
      payload: {},
    });
  }, []);

  const handleSortChange = useCallback((newSort: SortConfig) => {
    dispatch({ type: ApplicationsTableActionType.UpdateSort, payload: { sort: newSort } });
  }, []);

  const handleDeleteApplication = useCallback(() => {
    if (clickedItem) {
      setDeleteLoading(true);
      ApplicationsService.deleteApplication(clickedItem.id)
        .then(() => {
          handleDeleteModalClose();
          refetch();
          enqueueSnackbar('Application successfully deleted', { variant: 'success' });
        })
        .catch((errorMessage: string) => {
          enqueueSnackbar(errorMessage, defaultSnackbarErrorProps);
        })
        .finally(() => setDeleteLoading(false));
    }
  }, [clickedItem, enqueueSnackbar, handleDeleteModalClose, refetch]);

  const columns: Array<TableColumn<Application>> = useMemo(
    () => [
      {
        dataIndex: 'status',
        label: 'Status',
        align: 'center',
        rowAlign: 'right',
        sortable: true,
        render: (status: ApplicationStatus, record: Application) =>
          getApplicationStatusLabel(status, record, applicationProgressCalculations),
      },
      {
        dataIndex: 'id',
        label: 'Application #',
        sortable: true,
      },
      {
        dataIndex: 'name',
        label: 'Name',
        sortable: true,
      },
      {
        dataIndex: 'customerName',
        label: 'Applicant',
        hidden: !showApplicant,
      },
      {
        dataIndex: 'createdByName',
        label: 'Created By',
        hidden: !showApplicant,
      },
      {
        dataIndex: 'submittedDate',
        label: 'Submitted',
        sortable: true,
        align: 'center',
        render: (date: string) => getStringValue(date, NUMBER_DATE_FORMAT),
      },
      {
        dataIndex: 'dueDate',
        label: 'Due Date',
        align: 'center',
        sortable: true,
        render: (date?: string) => getStringValue(date, NUMBER_DATE_FORMAT),
      },
      {
        key: 'actions',
        label: 'Actions',
        align: 'center',
        render: (_: any, record: Application) => {
          const { id, status, isPayable, decisionDate } = record;

          switch (status) {
            case ApplicationStatus.Approved:
            case ApplicationStatus.Denied:
              return (
                <Grid {...defaultGridContainerProps} spacing={2} justifyContent={'center'}>
                  <Grid {...defaultGridItemProps} lg={6} className={styles.actionButtonWrapper}>
                    <ApplicationsTableActionButton
                      actionLabel={'View'}
                      nameLabel={'Decision'}
                      disabled={!decisionDate}
                      className={styles.actionButton}
                      onClick={handleDecisionModalOpen(record)}
                    />
                  </Grid>
                  <Grid {...defaultGridItemProps} lg={6} className={styles.actionButtonWrapper}>
                    <ApplicationsTableActionButton
                      href={getHashRouteUrl(`${routes.viewApplication}/${id}`)}
                      actionLabel={'View'}
                      nameLabel={'Application'}
                    />
                  </Grid>
                </Grid>
              );
            case ApplicationStatus.Draft:
              return (
                <Grid {...defaultGridContainerProps} spacing={2} justifyContent={'center'}>
                  <Grid {...defaultGridItemProps} lg={6} className={styles.actionButtonWrapper}>
                    <ApplicationsTableActionButton
                      href={getHashRouteUrl(`${routes.editApplication}/${id}`)}
                      actionLabel={'Edit'}
                      nameLabel={'Application'}
                    />
                  </Grid>
                  <Grid {...defaultGridItemProps} lg={6} className={styles.actionButtonWrapper}>
                    <Button
                      size={'large'}
                      color={'primary'}
                      variant={'outlined'}
                      onClick={handleDeleteModalOpen(record)}
                      className={commonStyles.dangerButtonOutlined}
                      aria-label={`Delete draft application ${record.id}`}
                      title="Delete draft application"
                      disabled={record.preventDeletionOfDraft}
                    >
                      <DeleteOutlined className={styles.icon} />
                    </Button>
                  </Grid>
                </Grid>
              );
            case ApplicationStatus.InformationRequired:
              return (
                <Grid {...defaultGridContainerProps} spacing={2} justifyContent={'center'}>
                  <Grid {...defaultGridItemProps} lg={6} className={styles.actionButtonWrapper}>
                    <ApplicationsTableActionButton
                      actionLabel={'View'}
                      nameLabel={'Decision'}
                      disabled={!decisionDate}
                      className={styles.actionButton}
                      onClick={handleDecisionModalOpen(record)}
                    />
                  </Grid>
                  <Grid {...defaultGridItemProps} lg={6} className={styles.actionButtonWrapper}>
                    <ApplicationsTableActionButton
                      href={getHashRouteUrl(`${routes.editApplication}/${id}`)}
                      actionLabel={'Edit'}
                      nameLabel={'Application'}
                      className={styles.actionButton}
                    />
                  </Grid>
                </Grid>
              );
            case ApplicationStatus.PendingPayment:
              return (
                isPayable && (
                  <ApplicationsTableActionButton
                    href={getHashRouteUrl(`${routes.payApplication}/${id}`)}
                    actionLabel={'Pay'}
                  />
                )
              );
            case ApplicationStatus.UnderReview:
            case ApplicationStatus.Submitted:
              return (
                <ApplicationsTableActionButton
                  href={getHashRouteUrl(`${routes.viewApplication}/${id}`)}
                  actionLabel={'View'}
                  nameLabel={'Application'}
                />
              );
            default:
              return null;
          }
        },
      },
    ],
    [applicationProgressCalculations, handleDecisionModalOpen, handleDeleteModalOpen, showApplicant]
  );

  const showChildren = useMemo(() => {
    return list.some((item) => item.children && item.children.length > 0);
  }, [list]);

  return (
    <>
      <Grid container={true} spacing={2}>
        <Grid {...defaultGridItemProps}>
          <TableCountLabel viewCount={list.length} totalCount={initialList.length} loading={loading} />
        </Grid>
        <Grid {...defaultGridItemProps}>
          <Table
            columns={columns}
            list={list}
            sort={sort}
            onSortChange={handleSortChange}
            loading={loading}
            showChildren={showChildren}
          />
        </Grid>
      </Grid>
      <Modal
        title={'Delete Draft Application'}
        open={deleteModalOpen}
        maxWidth={'xs'}
        loading={deleteLoading}
        actions={
          <>
            <Button color={'secondary'} variant={'outlined'} onClick={handleDeleteModalClose} disabled={deleteLoading}>
              {'Cancel'}
            </Button>
            <Button
              type={'submit'}
              disabled={deleteLoading}
              onClick={handleDeleteApplication}
              variant={'contained'}
              className={commonStyles.dangerButtonContained}
            >
              {'Delete'}
            </Button>
          </>
        }
      >
        <p className={commonStyles.text}>{'The draft application will be deleted permanently'}</p>
      </Modal>
      {clickedItem?.decisionDate && (
        <ApplicationDecisionModal
          onClose={handleDecisionModalClose}
          open={decisionModalOpen}
          application={clickedItem}
          showNavigation={true}
        />
      )}
    </>
  );
};
export default UserApplicationsTable;
